// src/pages/admin/AdminMonitoringPage.jsx
import { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, Chip, Alert, CircularProgress, LinearProgress, Tooltip,
  Button, Divider, IconButton
} from "@mui/material";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon  from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import RefreshIcon       from "@mui/icons-material/Refresh";
import WifiIcon          from "@mui/icons-material/Wifi";
import WifiOffIcon       from "@mui/icons-material/WifiOff";
import SpeedIcon         from "@mui/icons-material/Speed";
import MemoryIcon        from "@mui/icons-material/Memory";
import StorageIcon       from "@mui/icons-material/Storage";
import DnsIcon           from "@mui/icons-material/Dns";
import HistoryIcon       from "@mui/icons-material/History";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import ElectricBoltIcon     from "@mui/icons-material/ElectricBolt";
import ReplayIcon           from "@mui/icons-material/Replay";
import StopCircleIcon       from "@mui/icons-material/StopCircle";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { useOrg } from "../../context/OrgContext";
import { pageWrap } from "../../utils/pageStyles";

// ── Helpers ────────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const ok  = ["ok","healthy","recovered"].includes(status);
  const err = ["error","down"].includes(status);
  const color = ok ? "#22c55e" : err ? "#ef4444" : "#f59e0b";
  const icon  = ok ? <CheckCircleIcon sx={{ fontSize: 14, color }} />
              : err ? <ErrorOutlineIcon sx={{ fontSize: 14, color }} />
              : <WarningAmberIcon sx={{ fontSize: 14, color }} />;
  return (
    <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5,
      px:1.5, py:0.4, borderRadius:2,
      bgcolor: ok?"#f0fdf4":err?"#fef2f2":"#fffbeb",
      color, fontSize:11, fontWeight:700 }}>
      {icon} {String(status||"unknown").toUpperCase()}
    </Box>
  );
}

function SectionCard({ title, children, loading, icon, action }) {
  return (
    <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider",
      borderRadius:3, overflow:"hidden", mb:2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px:3, py:2, borderBottom:"1px solid", borderColor:"divider" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {loading && <CircularProgress size={14} />}
          {action}
        </Stack>
      </Stack>
      <Box sx={{ p:3 }}>{children}</Box>
    </Paper>
  );
}

function LogRow({ log }) {
  const level = (log.level || log.action || "").toUpperCase();
  const isCrit   = level.includes("CRITICAL") || level.includes("DOWN");
  const isWarn   = level.includes("WARNING")  || level.includes("DEGRADED");
  const isInfo   = level.includes("INFO")     || level.includes("RECOVERED");

  const chipBg    = isCrit ? "#fef2f2" : isWarn ? "#fffbeb" : "#f0f9ff";
  const chipColor = isCrit ? "#ef4444" : isWarn ? "#f59e0b" : "#0ea5e9";
  const label     = isCrit ? "CRITICAL" : isWarn ? "WARNING" : "INFO";

  const ts = log.created_at || log.timestamp;

  return (
    <Box sx={{ py:1.25, px:0, borderBottom:"1px solid #f1f5f9",
      "&:last-child":{ borderBottom:0 } }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Chip size="small" label={label}
          sx={{ height:18, fontSize:10, fontWeight:700,
            bgcolor:chipBg, color:chipColor, flexShrink:0, mt:0.25 }} />
        <Box sx={{ flex:1, minWidth:0 }}>
          <Typography variant="caption" sx={{ display:"block", wordBreak:"break-word",
            color:"text.primary", lineHeight:1.5 }}>
            {log.message || log.msg || JSON.stringify(log)}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt:0.5 }}>
            {log.action && (
              <Typography variant="caption" sx={{ color:"#6366f1", fontSize:10, fontFamily:"monospace" }}>
                {log.action}
              </Typography>
            )}
            {ts && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize:10 }}>
                {new Date(ts).toLocaleString("fr")}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}


// ── Jauge ressource ────────────────────────────────────────────────────────
function ResourceGauge({ label, percent, total, free, unit = "Go", icon }) {
  const pct  = (percent !== null && percent !== undefined) ? Math.round(Number(percent)) : null;
  const used = total != null && free != null ? Math.round((total - free) * 10) / 10 : null;
  const color = pct == null ? "#94a3b8"
              : pct > 85 ? "#ef4444"
              : pct > 65 ? "#f59e0b"
              : "#22c55e";
  return (
    <Box sx={{ flex: 1, minWidth: 160, bgcolor: "#f8fafc", borderRadius: 2,
      p: 2, border: "1px solid #e2e8f0" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {icon}
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="body2" fontWeight={700} sx={{ color }}>
          {pct !== null ? `${pct}%` : "—"}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct ?? 0}
        sx={{ height: 8, borderRadius: 4, bgcolor: "#e2e8f0",
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4, transition: "width .5s" } }} />
      {(used != null && total != null) && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            Utilisé : {used} {unit}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            Total : {Math.round(total * 10) / 10} {unit}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}


// ── Pagination locale ──────────────────────────────────────────────────────
function PaginatedSection({ items = [], pageSize = 10, renderItem, emptyText = "Aucun élément." }) {
  const [page, setPage] = useState(0);
  const total      = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const slice      = items.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <Box>
      {total === 0
        ? <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
        : slice.map((item, i) => renderItem(item, i))
      }
      {total > pageSize && (
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
          <Typography variant="caption" color="text.secondary">
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} sur {total}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="outlined" disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              sx={{ borderRadius: 2, fontSize: 11, minWidth: 32, px: 1 }}>←</Button>
            {Array.from({ length: totalPages }, (_, i) => (
              <Button key={i} size="small"
                variant={i === page ? "contained" : "outlined"}
                onClick={() => setPage(i)}
                sx={{
                  borderRadius: 2, fontSize: 11, minWidth: 32, px: 1,
                  ...(i === page ? {
                    bgcolor: "#6366f1", color: "#fff",
                    "&:hover": { bgcolor: "#4f46e5" }
                  } : {}),
                }}>
                {i + 1}
              </Button>
            )).slice(
              // Afficher max 5 boutons centrés sur la page courante
              Math.max(0, Math.min(page - 2, totalPages - 5)),
              Math.max(5, Math.min(page + 3, totalPages))
            )}
            <Button size="small" variant="outlined" disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              sx={{ borderRadius: 2, fontSize: 11, minWidth: 32, px: 1 }}>→</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// Section : Réservations bloquées (Running + Expired)
// ─────────────────────────────────────────────────────────────────────────────
function StuckReservationsSection() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [stopping, setStopping] = useState({});
  const [error,    setError]    = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      // Récupérer toutes les réservations running dont end_time est passé
      const { data } = await api.get("/admin/reservations", {
        params: { status_filter: "running", page: 1, page_size: 100 }
      });
      const now = Date.now();
      const stuck = (data?.items || []).filter(r => {
        const end = r.end_time ? new Date(r.end_time).getTime() : null;
        return end && end < now;
      });
      setItems(stuck);
    } catch { setError("Impossible de charger les réservations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const forceStop = async (id) => {
    setStopping(p => ({ ...p, [id]: true }));
    try {
      await api.post(`/admin/system/reservations/${id}/force-stop`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur arrêt forcé");
    } finally {
      setStopping(p => ({ ...p, [id]: false }));
    }
  };

  return (
    <SectionCard
      title="Réservations bloquées (Running + Expirées)"
      icon={<StopCircleIcon sx={{ fontSize: 18, color: "#ef4444" }} />}
      action={
        <Button size="small" onClick={load} disabled={loading}
          startIcon={loading ? <CircularProgress size={12} /> : null}
          sx={{ borderRadius: 2, fontSize: 12 }}>
          Vérifier
        </Button>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Labs encore <strong>Running</strong> dont la date de fin est dépassée.
          Le scheduler aurait dû les arrêter — utilisez "Arrêt forcé" si ce n'est pas le cas.
        </Typography>

        {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
        {error   && <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>{error}</Alert>}

        {!loading && items.length === 0 && (
          <Alert severity="success" sx={{ borderRadius: 2, fontSize: 13 }}>
            ✅ Aucune réservation bloquée — tout est propre.
          </Alert>
        )}

        {items.length > 0 && (
          <Stack spacing={1}>
            {items.map(r => (
              <Box key={r.id} sx={{
                display: "grid",
                gridTemplateColumns: "60px 1fr 160px 140px 130px",
                gap: 1, px: 2, py: 1.25,
                border: "1px solid #fee2e2", borderRadius: "8px", bgcolor: "#fff5f5",
                alignItems: "center",
              }}>
                <Typography sx={{ fontSize: 11, color: "#ef4444", fontWeight: 700 }}>
                  #{r.id}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.lab_name || r.lab_id}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  {r.username || `user#${r.user_id}`}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>
                  Expirée {r.end_time
                    ? new Date(r.end_time).toLocaleString("fr")
                    : "—"}
                </Typography>
                <Tooltip title="Forcer l'arrêt du lab et clôturer la réservation">
                  <span>
                    <Button size="small" variant="contained"
                      onClick={() => forceStop(r.id)}
                      disabled={stopping[r.id]}
                      startIcon={stopping[r.id]
                        ? <CircularProgress size={12} color="inherit" />
                        : <StopCircleIcon sx={{ fontSize: 14 }} />}
                      sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" },
                        borderRadius: "8px", fontSize: 11, textTransform: "none" }}>
                      {stopping[r.id] ? "Arrêt…" : "Arrêt forcé"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section : Nettoyage des wallets expirés
// ─────────────────────────────────────────────────────────────────────────────
function WalletCleanupSection() {
  const [preview,   setPreview]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [cleaning,  setCleaning]  = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState("");

  const loadPreview = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get("/admin/system/wallets/expired");
      setPreview(data);
    } catch { setError("Impossible de charger les wallets expirés"); }
    finally { setLoading(false); }
  };

  const runCleanup = async () => {
    setCleaning(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/admin/system/wallets/cleanup");
      setResult(data);
      // Rafraîchir la prévisualisation
      const { data: fresh } = await api.get("/admin/system/wallets/expired");
      setPreview(fresh);
    } catch (e) { setError(e?.response?.data?.detail || "Erreur lors du nettoyage"); }
    finally { setCleaning(false); }
  };

  const count = preview?.count ?? null;

  return (
    <SectionCard
      title="Nettoyage des wallets expirés"
      icon={<AccountBalanceWalletIcon sx={{ fontSize: 18, color: "#6366f1" }} />}
      action={
        <Button size="small" onClick={loadPreview} disabled={loading}
          startIcon={loading ? <CircularProgress size={12} /> : null}
          sx={{ borderRadius: 2, fontSize: 12 }}>
          Vérifier
        </Button>
      }
    >
      <Stack spacing={2}>
        {/* Description */}
        <Typography variant="body2" color="text.secondary">
          Désactive les wallets dont la date d'expiration est passée mais qui sont encore marqués actifs en base.
          Le cron s'en charge chaque nuit à 02:00 UTC — ce bouton permet de l'exécuter manuellement.
        </Typography>

        {loading && <LinearProgress sx={{ borderRadius: 1 }} />}

        {/* Résultat du dernier nettoyage */}
        {result && (
          <Alert severity={result.deactivated > 0 ? "success" : "info"} sx={{ borderRadius: 2, fontSize: 13 }}>
            {result.message}
            {result.ran_at && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "inherit", opacity: 0.8 }}>
                Exécuté le {new Date(result.ran_at).toLocaleString("fr")}
              </Typography>
            )}
          </Alert>
        )}

        {/* Prévisualisation */}
        {preview !== null && !loading && (
          <Box>
            {count === 0 ? (
              <Alert severity="success" sx={{ borderRadius: 2, fontSize: 13 }}>
                ✅ Aucun wallet expiré actif — la base est propre.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip size="small" label={`${count} wallet${count > 1 ? "s" : ""} expiré${count > 1 ? "s" : ""}`}
                    sx={{ bgcolor: "#fef2f2", color: "#ef4444", fontWeight: 700 }} />
                  <Typography variant="caption" color="text.secondary">
                    Vérifié le {preview.checked_at ? new Date(preview.checked_at).toLocaleString("fr") : "—"}
                  </Typography>
                </Stack>

                {/* Table des wallets */}
                <Box sx={{ border: "1px solid #f1f5f9", borderRadius: 2, overflow: "hidden" }}>
                  <Box sx={{ bgcolor: "#f8fafc", px: 2, py: 1,
                    display: "grid", gridTemplateColumns: "1fr 1fr 80px 100px 120px",
                    gap: 1 }}>
                    {["Utilisateur", "Pack", "Restant", "Utilisé", "Expiré le"].map(h => (
                      <Typography key={h} variant="caption" fontWeight={700} color="text.secondary">
                        {h}
                      </Typography>
                    ))}
                  </Box>
                  {(preview.items || []).slice(0, 10).map(w => (
                    <Box key={w.id} sx={{
                      px: 2, py: 0.75, borderTop: "1px solid #f1f5f9",
                      display: "grid", gridTemplateColumns: "1fr 1fr 80px 100px 120px",
                      gap: 1, alignItems: "center",
                    }}>
                      <Typography variant="caption" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {w.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {w.pack_code || "—"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 600 }}>
                        {w.remaining_minutes} min
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {w.used_minutes} min
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {w.expires_at ? new Date(w.expires_at).toLocaleDateString("fr") : "—"}
                      </Typography>
                    </Box>
                  ))}
                  {count > 10 && (
                    <Box sx={{ px: 2, py: 1, borderTop: "1px solid #f1f5f9", bgcolor: "#fffbeb" }}>
                      <Typography variant="caption" color="text.secondary">
                        … et {count - 10} autres wallets non affichés
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Bouton lancer le nettoyage */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Tooltip title={`Désactiver ${count} wallet${count > 1 ? "s" : ""} expirés`}>
                    <span>
                      <Button variant="contained" onClick={runCleanup} disabled={cleaning}
                        startIcon={cleaning ? <CircularProgress size={14} color="inherit" /> : <CleaningServicesIcon sx={{ fontSize: 16 }} />}
                        sx={{ borderRadius: 2, bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}>
                        {cleaning ? "Nettoyage…" : `Désactiver ${count} wallet${count > 1 ? "s" : ""}`}
                      </Button>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    Cette action est irréversible — les wallets désactivés ne seront pas restaurés automatiquement.
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>{error}</Alert>}
      </Stack>
    </SectionCard>
  );
}

export default function AdminMonitoringPage() {
  const { t } = useTranslation();
  const [health,    setHealth]    = useState(null);
  const [logs,      setLogs]      = useState(null);
  const [gns3Hist,  setGns3Hist]  = useState(null);
  const [loadH,     setLoadH]     = useState(false);
  const [loadL,     setLoadL]     = useState(false);
  const [loadG,     setLoadG]     = useState(false);
  const [resources, setResources] = useState(null);
  const [loadR,     setLoadR]     = useState(false);
  const [errors500, setErrors500] = useState(null);
  const [loadE,     setLoadE]     = useState(false);
  const [error,     setError]     = useState("");

  const fetchHealth = useCallback(async () => {
    setLoadH(true); setError("");
    try {
      const { data } = await api.get("/admin/system/health");
      setHealth(data);
    } catch { setError("Impossible de charger le statut système"); }
    finally { setLoadH(false); }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadL(true);
    try {
      const { data } = await api.get("/admin/system/logs", { params: { limit: 200 } });
      // data = { deployment_logs: [...], activity_logs: [...] }
      const acts   = data?.activity_logs   || [];
      const deploys= data?.deployment_logs || [];
      // Fusionner et trier par date desc
      const merged = [...acts, ...deploys].sort((a, b) =>
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setLogs(merged.slice(0, 30));
    } catch { /* non critique */ }
    finally { setLoadL(false); }
  }, []);

  const fetchGns3History = useCallback(async () => {
    setLoadG(true);
    try {
      const { data } = await api.get("/admin/system/gns3-history", { params: { limit: 200 } });
      setGns3Hist(data);
    } catch { /* non critique — route peut ne pas encore exister */ }
    finally { setLoadG(false); }
  }, []);

  const fetchResources = useCallback(async () => {
    setLoadR(true);
    try {
      const { data } = await api.get("/admin/system/gns3-resources");
      console.log("[GNS3 Resources]", JSON.stringify(data, null, 2));
    setResources(data);
    } catch (e) { console.error("[GNS3 Resources error]", e); }
    finally { setLoadR(false); }
  }, []);

  const fetchErrors = useCallback(async () => {
    setLoadE(true);
    try {
      const { data } = await api.get("/admin/system/errors", { params: { limit: 30 } });
      setErrors500(data);
    } catch { /* non critique */ }
    finally { setLoadE(false); }
  }, []);

  const refresh = useCallback(() => {
    fetchHealth(); fetchLogs(); fetchGns3History(); fetchResources(); fetchErrors();
  }, [fetchHealth, fetchLogs, fetchGns3History, fetchResources, fetchErrors]);

  useEffect(() => { refresh(); }, [refresh]);

  const gns3 = health?.gns3 || {};
  const sys  = health?.system || {};
  const hc   = gns3?.healthcheck || {};

  return (
    <Box sx={pageWrap}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t("admin.monitoring_title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            Santé système · GNS3 · Logs récents
          </Typography>
        </Box>
        <Tooltip title={t("common.refresh")}>
          <IconButton onClick={refresh} disabled={loadH||loadL||loadG} size="small"
            sx={{ border:"1px solid", borderColor:"divider", borderRadius:2 }}>
            {(loadH||loadL||loadG) ? <CircularProgress size={16}/> : <RefreshIcon fontSize="small"/>}
          </IconButton>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" onClose={()=>setError("")} sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

      {/* ── Statut GNS3 ───────────────────────────────────────────── */}
      <SectionCard
        title="GNS3"
        loading={loadH}
        icon={gns3.api_status==="ok"
          ? <WifiIcon sx={{ fontSize:18, color:"#22c55e" }}/>
          : <WifiOffIcon sx={{ fontSize:18, color:"#ef4444" }}/>}
      >
        {!health && !loadH
          ? <Typography color="text.secondary">Chargement…</Typography>
          : (
          <Stack spacing={2}>
            {/* Statut + latence */}
            <Stack direction={{ xs:"column", sm:"row" }} spacing={2}>
              <Box sx={{ flex:1, bgcolor:"#f8fafc", borderRadius:2, p:2, border:"1px solid #e2e8f0" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>STATUT API</Typography>
                  <StatusDot status={gns3.api_status || "unknown"} />
                </Stack>
                {gns3.version && (
                  <Typography variant="caption" color="text.secondary">
                    Version : <strong>{gns3.version}</strong>
                  </Typography>
                )}
                {gns3.error && (
                  <Typography variant="caption" color="error.main" sx={{ display:"block", mt:0.5, fontSize:11 }}>
                    {String(gns3.error).slice(0, 120)}
                  </Typography>
                )}
              </Box>

              {gns3.latency_ms != null && (
                <Box sx={{ flex:1, bgcolor:"#f8fafc", borderRadius:2, p:2, border:"1px solid #e2e8f0" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>LATENCE</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <SpeedIcon sx={{ fontSize:14, color:"#6366f1" }}/>
                      <Typography variant="body2" fontWeight={700} sx={{
                        color: gns3.latency_ms > 800 ? "#ef4444" : gns3.latency_ms > 400 ? "#f59e0b" : "#22c55e"
                      }}>
                        {gns3.latency_ms} ms
                      </Typography>
                    </Stack>
                  </Stack>
                  <LinearProgress variant="determinate"
                    value={Math.min(100, (gns3.latency_ms / 1000) * 100)}
                    sx={{ height:5, borderRadius:3, bgcolor:"#e2e8f0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: gns3.latency_ms > 800 ? "#ef4444" : gns3.latency_ms > 400 ? "#f59e0b" : "#22c55e",
                        borderRadius:3 }}} />
                </Box>
              )}
            </Stack>

            {/* Job healthcheck */}
            {hc.last_check_at && (
              <Box sx={{ bgcolor:"#f8fafc", borderRadius:2, p:2, border:"1px solid #e2e8f0" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display:"block", mb:1 }}>
                  MONITORING PÉRIODIQUE (60s)
                </Typography>
                <Stack direction="row" flexWrap="wrap" spacing={2}>
                  {[
                    ["Dernier check", new Date(hc.last_check_at).toLocaleString("fr")],
                    ["Dernier statut", (hc.last_status||"—").toUpperCase()],
                    ["Échecs consécutifs", hc.consecutive_failures ?? 0],
                    ["Latence", hc.last_latency_ms != null ? `${hc.last_latency_ms} ms` : "—"],
                  ].map(([k, v]) => (
                    <Box key={k}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize:10 }}>{k}</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ display:"block" }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
                {hc.first_failure_at && (
                  <Typography variant="caption" color="error.main" sx={{ mt:1, display:"block", fontSize:11 }}>
                    Panne depuis : {new Date(hc.first_failure_at).toLocaleString("fr")}
                  </Typography>
                )}
              </Box>
            )}

            {/* Stats système */}
            {sys && Object.keys(sys).length > 0 && (
              <>
                <Divider />
                <Stack direction="row" flexWrap="wrap" spacing={2}>
                  {[
                    ["Utilisateurs", sys.total_users],
                    ["Réservations", sys.total_reservations],
                    ["Labs actifs", sys.running_jobs],
                    ["Échecs 24h", sys.failed_jobs_24h],
                    ["Logs 24h", sys.activity_logs_24h],
                  ].map(([k, v]) => v != null && (
                    <Box key={k} sx={{ textAlign:"center", minWidth:80 }}>
                      <Typography variant="h6" fontWeight={700} sx={{
                        color: (k==="Échecs 24h" && v>0) ? "#ef4444" : "text.primary"
                      }}>{v}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize:10 }}>{k}</Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </SectionCard>


      {/* ── Ressources serveur GNS3 ────────────────────────────────── */}
      <SectionCard
        title="Ressources serveur GNS3"
        loading={loadR}
        icon={<DnsIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
        action={
          <Tooltip title="Rafraîchir">
            <IconButton size="small" onClick={fetchResources} disabled={loadR}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        }
      >
        {!resources && !loadR && (
          <Typography variant="body2" color="text.secondary">
            Chargement des ressources…
          </Typography>
        )}
        {resources?.error && (
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12 }}>
            Ressources indisponibles : {resources.error}
          </Alert>
        )}
        {resources && !resources.error && (() => {
          const computes = resources.computes?.length > 0 ? resources.computes : [resources];
          return (
            <Stack spacing={3}>
              {computes.map((comp, i) => (
                <Box key={comp.compute_id || i}>
                  {computes.length > 1 && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <DnsIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: .5, fontSize: 10 }}>
                        {comp.compute_name || comp.compute_id || `Compute ${i + 1}`}
                      </Typography>
                      {comp.error && (
                        <Chip size="small" label="Indisponible"
                          sx={{ height: 16, fontSize: 9, bgcolor: "#fef2f2", color: "#ef4444" }} />
                      )}
                    </Stack>
                  )}
                  {comp.error
                    ? <Typography variant="caption" color="error.main" sx={{ fontSize: 11 }}>
                        {comp.error}
                      </Typography>
                    : (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
                      <ResourceGauge
                        label="CPU"
                        percent={comp.cpu_usage_percent}
                        icon={<SpeedIcon sx={{ fontSize: 14, color: "text.secondary" }} />}
                      />
                      <ResourceGauge
                        label="RAM"
                        percent={comp.memory_usage_percent}
                        total={comp.memory_total_gb}
                        free={comp.memory_free_gb}
                        icon={<MemoryIcon sx={{ fontSize: 14, color: "text.secondary" }} />}
                      />
                      <ResourceGauge
                        label="Disque"
                        percent={comp.disk_usage_percent}
                        total={comp.disk_total_gb}
                        free={comp.disk_free_gb}
                        icon={<StorageIcon sx={{ fontSize: 14, color: "text.secondary" }} />}
                      />
                    </Stack>
                  )}
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {computes.length} compute{computes.length > 1 ? "s" : ""} · Mis à jour à {new Date().toLocaleTimeString("fr")}
              </Typography>
            </Stack>
          );
        })()}
      </SectionCard>

      {/* ── Historique incidents GNS3 ──────────────────────────────── */}
      {(gns3Hist?.items?.length > 0 || loadG) && (
        <SectionCard
          title={`Incidents GNS3${gns3Hist?.incidents_24h > 0 ? ` · ${gns3Hist.incidents_24h} dans les 24h` : ""}`}
          loading={loadG}
          icon={<HistoryIcon sx={{ fontSize:18, color:"text.secondary" }}/>}
        >
          <PaginatedSection
            items={gns3Hist?.items || []}
            pageSize={10}
            emptyText="Aucun incident GNS3 enregistré."
            renderItem={(log, i) => <LogRow key={i} log={log} />}
          />
        </SectionCard>
      )}

      {/* ── Erreurs 500 ────────────────────────────────────────────── */}
      {(errors500?.items?.length > 0 || loadE) && (
        <SectionCard
          title={`Erreurs API 500${errors500?.items?.length > 0 ? ` (${errors500.items.length})` : ""}`}
          loading={loadE}
          icon={<ErrorOutlineIcon sx={{ fontSize: 18, color: "#ef4444" }} />}
        >
          <PaginatedSection
            items={errors500?.items || []}
            pageSize={5}
            emptyText="Aucune erreur 500 enregistree."
            renderItem={(err, i) => (
              <Box key={i} sx={{ py: 1.25, borderBottom: "1px solid #f1f5f9", "&:last-child": { borderBottom: 0 } }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Chip size="small" label="500"
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                      bgcolor: "#fef2f2", color: "#ef4444", flexShrink: 0, mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: "block", wordBreak: "break-word", color: "text.primary", lineHeight: 1.5 }}>
                      {err.message}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                      {err.details?.path && (
                        <Typography variant="caption" sx={{ color: "#6366f1", fontSize: 10, fontFamily: "monospace" }}>
                          {err.details.method} {err.details.path}
                        </Typography>
                      )}
                      {err.request_id && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>id:{err.request_id}</Typography>
                      )}
                      {err.created_at && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                          {new Date(err.created_at).toLocaleString("fr")}
                        </Typography>
                      )}
                    </Stack>
                    {err.details?.traceback && (
                      <Box sx={{ mt: 0.75, bgcolor: "#0f172a", borderRadius: 1, p: 1,
                        fontFamily: "monospace", fontSize: 10, color: "#94a3b8",
                        whiteSpace: "pre-wrap", maxHeight: 120, overflowY: "auto" }}>
                        {err.details.traceback.slice(-800)}
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}
          />
        </SectionCard>
      )}

      {/* ── Réservations bloquées ─────────────────────────────────── */}
      <StuckReservationsSection />

      {/* ── Nettoyage wallets expirés ──────────────────────────────── */}
      <WalletCleanupSection />

      {/* ── Logs récents ───────────────────────────────────────────── */}
      <SectionCard title={t("admin.recent_logs")} loading={loadL}>
        {loadL
          ? <Typography color="text.secondary">Chargement…</Typography>
          : <PaginatedSection
              items={logs || []}
              pageSize={10}
              emptyText={t("admin.no_activity")}
              renderItem={(log, i) => <LogRow key={i} log={log} />}
            />
        }
      </SectionCard>
    </Box>
  );
}

